import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { Link } from "./Link";

export const buttonRoundSmall = tv({
  base: cn(
    "uipm:inline-flex uipm:items-center uipm:justify-center",
    "uipm:w-[50px] uipm:h-[50px] uipm:min-w-[50px]",
    "uipm:p-4 uipm:gap-2",
    "uipm:rounded-full",
    "uipm:text-[var(--Typography-Sizes-body-medium,16px)] uipm:leading-[var(--Typography-Line-heights-body-medium,26px)]",
    "uipm:font-semibold",
    "uipm:font-[var(--font-family-sans)]",
    "uipm:transition-all uipm:duration-200 uipm:ease-out",
    "uipm:no-underline",
    "uipm:text-center",
    "uipm:cursor-pointer",
  ),
  variants: {
    variant: {
      default: cn(
        "uipm:bg-transparent",
        "uipm:text-[var(--Typography-Colors-text-faded-dark,#575757)]",
        "uipm:hover:bg-[var(--surface-surface-secondary)]",
        "uipm:active:bg-[var(--surface-surface-light)]",
      ),
      active: cn(
        "uipm:bg-[var(--surface-surface-accent)]",
        "uipm:text-[var(--typography-colors-text-inverted)]",
        "uipm:hover:bg-[var(--color-color-accent-hover)]",
        "uipm:active:bg-[var(--color-color-accent-focus)]",
      ),
      disabled: cn(
        "uipm:bg-transparent",
        "uipm:text-[var(--typography-colors-text-disabled)]",
        "uipm:pointer-events-none",
        "uipm:opacity-50",
        "uipm:cursor-default",
      ),
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export type ButtonRoundSmallProps = VariantProps<typeof buttonRoundSmall> & {
  className?: string;
  children?: React.ReactNode;
  href?: string;
  onClick?: React.MouseEventHandler;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-current"?: "page" | undefined;
  "aria-disabled"?: boolean | "true" | "false";
  noFollow?: boolean;
};

export const ButtonRoundSmall = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonRoundSmallProps
>(
  (
    {
      className,
      variant,
      href,
      children,
      onClick,
      noFollow,
      disabled,
      ...props
    },
    ref,
  ) => {
    const buttonClassName = cn(buttonRoundSmall({ variant }), className);

    if (href && !disabled) {
      return (
        <Link
          href={href}
          className={buttonClassName}
          ref={ref as React.Ref<HTMLAnchorElement>}
          onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
          noFollow={noFollow}
          {...props}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        disabled={disabled}
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        className={buttonClassName}
        {...props}
      >
        {children}
      </button>
    );
  },
);

ButtonRoundSmall.displayName = "ButtonRoundSmall";
